<template>
  <div class="min-h-screen bg-off-black">
    <div class="container mx-auto px-4 py-16">
      <!-- Hero Section -->
      <div class="text-center mb-16">
        <h1 class="text-5xl md:text-6xl font-serif font-semibold mb-6 text-vibe-green">
          Smart Onboarding for Australian Professionals
        </h1>
        <p class="text-xl md:text-2xl text-gray-green max-w-3xl mx-auto">
          Streamline AML compliance and client onboarding with AI-powered solutions designed for legal and accounting professionals operating in Australia
        </p>
      </div>

      <!-- Freebie Cards Section -->
      <div class="max-w-3xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-serif font-semibold text-white text-center mb-12">
          Free Resources
        </h2>

        <div class="grid md:grid-cols-2 gap-8">
          <!-- Smart Onboarding International Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative md:col-span-2">
            <div class="absolute top-4 right-4 bg-vibe-green text-black text-xs font-bold px-3 py-1 rounded-full">
              NEW
            </div>
            <div class="p-8">
              <div class="flex flex-col md:flex-row md:items-center gap-6">
                <div class="flex-shrink-0">
                  <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center">
                    <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                <div class="flex-1">
                  <h3 class="text-2xl font-semibold text-vibe-green mb-3">
                    Smart Onboarding for Australian Professionals
                  </h3>
                  <p class="text-gray-green mb-4 leading-relaxed">
                    A practical, visual resource designed to help you navigate AML compliance with confidence and clarity — tailored for the Australian market.
                  </p>
                  <ul class="text-sm text-gray-green mb-6 grid md:grid-cols-2 gap-2">
                    <li class="flex items-start">
                      <span class="text-vibe-green mr-2">✓</span>
                      <span>Risk-based onboarding strategies</span>
                    </li>
                    <li class="flex items-start">
                      <span class="text-vibe-green mr-2">✓</span>
                      <span>Sector-specific insights for Australia</span>
                    </li>
                    <li class="flex items-start">
                      <span class="text-vibe-green mr-2">✓</span>
                      <span>Practical tips on OSINT & HUMINT</span>
                    </li>
                    <li class="flex items-start">
                      <span class="text-vibe-green mr-2">✓</span>
                      <span>ECCTA reforms & onboarding automation</span>
                    </li>
                  </ul>
                  <router-link
                    to="/australia/smart-onboarding"
                    class="btn-primary inline-block text-center"
                  >
                    Download Free Guide
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Australia Beneficial Ownership Checklist & Cases Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative">
            <div class="absolute top-4 right-4 bg-vibe-green text-black text-xs font-bold px-3 py-1 rounded-full">
              AUSTRALIA
            </div>
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                Australia Beneficial Ownership Checklist & Cases
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                A practical checklist and real-world cases to help professionals navigate Australian beneficial ownership compliance.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Australian beneficial ownership requirements</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Checklist for client assessment & verification</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Real-world case studies & examples</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Practical implementation guidance</span>
                </li>
              </ul>

              <router-link
                to="/australia/beneficial-ownership-checklist"
                class="btn-primary w-full text-center inline-block"
              >
                Download Checklist & Cases
              </router-link>
            </div>
          </div>

          <!-- Australia AML/CTF 2026 Readiness Checklist Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative">
            <div class="absolute top-4 right-4 bg-vibe-green text-black text-xs font-bold px-3 py-1 rounded-full">
              NEW
            </div>
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                AML/CTF 2026 Readiness Checklist
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                A practical structure for firms preparing for the new AML/CTF obligations commencing 1 July 2026.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Governance & preparation framework</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>AML/CTF program development</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>CDD & Enhanced Due Diligence</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>AUSTRAC reporting & record keeping</span>
                </li>
              </ul>

              <router-link
                to="/australia/aml-ctf-2026-readiness-checklist"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Checklist
              </router-link>
            </div>
          </div>

          <!-- Australia AML Readiness Checklist Card -->
          <div class="bg-green-700 rounded-lg overflow-hidden shadow-xl border border-green-600 hover:border-vibe-green transition-all duration-300 hover:transform hover:scale-105 relative">
            <div class="absolute top-4 right-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              ESSENTIAL
            </div>
            <div class="p-8">
              <div class="bg-vibe-green rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>

              <h3 class="text-2xl font-semibold text-vibe-green mb-4">
                2026 AML Readiness Checklist
              </h3>

              <p class="text-gray-green mb-6 leading-relaxed">
                Comprehensive AML readiness checklist for Australian firms preparing for the latest 2026 compliance reforms.
              </p>

              <ul class="text-sm text-gray-green mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>AML readiness assessment</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>AUSTRAC compliance alignment</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Risk-based approach framework</span>
                </li>
                <li class="flex items-start">
                  <span class="text-vibe-green mr-2">✓</span>
                  <span>Practical implementation steps</span>
                </li>
              </ul>

              <router-link
                to="/australia/aml-readiness-checklist"
                class="btn-primary w-full text-center inline-block"
              >
                Download Free Checklist
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="max-w-4xl mx-auto mt-20 bg-green-600 rounded-lg p-12 text-center border border-vibe-green">
        <h2 class="text-3xl md:text-4xl font-serif font-semibold text-vibe-green mb-6">
          Ready to Automate Your Onboarding?
        </h2>
        <p class="text-lg text-white mb-8">
          See how Marker AI can help you streamline workflows, strengthen compliance, and accelerate commercial momentum. Book a demo to learn more.
        </p>
        <a
          href="https://www.marker-ai.com/contact-us"
          class="btn-primary text-lg"
        >
          Get in Touch
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AustraliaHub',
};
</script>
